import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/tenant-market/free-rent/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import freeRentService from "data/free-rent"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { handleReferences } from "data/lib/handle-references"
import { copy, paste, canPaste } from "data/lib/copy-paste"
// Styles
import { TMFreeRentTable_s } from "components/tenant-market/free-rent/tm-free-rent-styles"

type ContextRow = {
  freerentid: number
  propertyid: number
  code: string
  systeminput?: string
}

const TMFreeRentTable = (props: {tableId: string, data: object[], defaults: object}) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: "Trace", icon: "pi pi-fw pi-search", command: e=> {
      const refreshFn = () => freeRentService.fetchFreeRent(contextRow.propertyid)
      handleReferences("freerent", contextRow.freerentid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: "Duplicate Row", icon: "pi pi-fw pi-copy", command: e => {
      freeRentService.copy(contextRow.propertyid, contextRow.freerentid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const freerents = await freeRentService.copies(contextRow.propertyid, contextRow.freerentid, n)
      })
    }},
    {label: "Delete Row", icon: "pi pi-fw pi-trash", command: e => {
      const currentRowNum = getFocusedRowIndex() + 1
      showDeleteConfirmation(contextRow, currentRowNum)
    }},
    traceMenuItem
  ]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({freerentid: 0, propertyid: 0, code: ""})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field("freerent")

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const freeRentEventSubscription = freeRentService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "FREERENT_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "FREERENT_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            freeRentService.create(propertyService.getStoredPropertyId(), props.defaults)
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[(pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1];
            showDeleteConfirmation({freerentid: currentRow.freerentid, propertyid: currentRow.propertyid, code: currentRow.code}, currentRowNum)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      freeRentEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, "current.filter", "", "contains");
    else if (searchFilter !== "") _.invoke(tableRef, "current.filter", searchFilter, "contains");
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    let freeRent = _.omit(_.cloneDeep(rowData), 'index')

    if (field === "freerentdetail") {
      freeRent = value
    } else {
      _.set(freeRent, field, value)
    }

    freeRentService.patch(freeRent)
    if(value && field === "freerentdetail") carouselService.updateRowData(freeRent)
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, freeRentService.fetchFreeRent, 'freerent')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([traceMenuItem]) : setMenu(nonSystemMenu)
  }, [contextCell])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    let pager = getPagingInfo(props.tableId)
    let row = currentRow + ((pager.currentPage - 1) * pager.pageSize);

    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        freeRentService.deleteOne(record.propertyid, record.freerentid)
        setDeleteConfirmation(null)
        automationService.broadcast("MODAL_CLOSING", {})
        setTimeout(() => focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0), 200)
      },
      rowNum: row,
      code: record.code
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.freerentid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  function updateContextMenu(originalEvent){
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => { return {"" : rowData} }

  const defaults:object = props.defaults;

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      {defaults && <TMFreeRentTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            header="Code"
            {...field("code")}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            header="Name"
            {...field("name")}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            header="Input Type"
            {...field("freerentdetail", (rowData, props) => { return (
              <CarouselTrigger
                field="freerentdetail"
                rowData={rowData}
                title="Input Type"
                persist={persist}
                defaults={defaults}
                componentAtRest={Controls.InputTypeDisplay}
                controls={{
                  basic: {control: Controls.InputTypeBasic, name: "Basic"},
                  detailed: {control: Controls.InputTypeDetailed, name: "Detailed"},
                  withinLease: {control: Controls.WithinLease, name: "Within Lease"}
                }}
                hover={{
                  basic: [{key: 'mosnew', label: 'New', ignoreField: true},{key: 'mosrenew', label: 'Renewal', ignoreField: true},{key: 'amttype', label: 'Amt Type', ignoreField: true},{key: 'freerentrevtype', label: 'Revenue', ignoreField: true}],
                  detailed: {single: [{key: ['freerentdetail', 'amtType'], label: 'Amt Type'}, {key: ['freerentdetail', 'revenue'], label: 'Revenue'}], multiple: [{key: ['freerentdetail', 'year'], label: 'Year'},{key: ['freerentdetail', 'new'], label: 'New'},{key: ['freerentdetail', 'renewal'], label: "Renewal"}]},
                  withinLease: {single: [{key: ['freerentdetail', 'amtType'], label: 'Amt Type'}, {key: ['freerentdetail', 'revenue'], label: 'Revenue'}], multiple: [{key: ['freerentdetail', 'month'], label: 'Month'},{key: ['freerentdetail', 'new'], label: 'New'},{key: ['freerentdetail', 'renewal'], label: "Renewal"}]}
                }}
              />)},
              (err) => !_.includes(["code", "name"], err.path[0])
            )}
          />
        </DataTable>
        { carouselData && carouselData["show"] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={680} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </TMFreeRentTable_s>}
    </div>
  )
}

export default TMFreeRentTable
